import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { readWithTimeout } from '../lib/supabaseRead';

// Web push subscription management (roadmap #2 P1).
//
// States:
//   'unsupported'      — browser has no push API at all
//   'install_required' — iOS Safari tab; push needs the installed (home screen) PWA
//   'denied'           — user blocked notifications at the OS/browser level
//   'off' | 'on'       — toggle states
//   'busy'             — a transition is in flight
//
// All Supabase calls are timeout-guarded per the post-incident house rules.

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

function detectSupport() {
  if (typeof window === 'undefined') return 'unsupported';
  const hasPush = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (hasPush) return 'supported';
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '');
  const standalone = window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true;
  if (isIOS && !standalone) return 'install_required';
  return 'unsupported';
}

export function usePushNotifications(userId) {
  const [status, setStatus] = useState('busy');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const support = detectSupport();
    if (support !== 'supported') {
      setStatus(support);
      return undefined;
    }
    if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
      setStatus('denied');
      return undefined;
    }
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (!cancelled) setStatus(sub ? 'on' : 'off');
      } catch {
        if (!cancelled) setStatus('off');
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  const enable = useCallback(async () => {
    if (!userId) return false;
    setError(null);
    setStatus('busy');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'off');
        return false;
      }
      const reg = await navigator.serviceWorker.ready;
      const { data: publicKey, error: keyError } = await readWithTimeout(
        signal => supabase.rpc('get_vapid_public_key').abortSignal(signal),
        { label: 'vapid_public_key' }
      );
      if (keyError || !publicKey) throw new Error(keyError?.message || 'Push keys unavailable');

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      const json = subscription.toJSON();
      const { error: saveError } = await readWithTimeout(
        signal => supabase.from('push_subscriptions').upsert({
          user_id: userId,
          endpoint: subscription.endpoint,
          p256dh: json.keys?.p256dh || '',
          auth: json.keys?.auth || '',
          platform: navigator.userAgent ? navigator.userAgent.slice(0, 120) : null,
        }, { onConflict: 'endpoint' }).abortSignal(signal),
        { label: 'push_subscription_save' }
      );
      if (saveError) throw new Error(saveError.message);
      setStatus('on');
      return true;
    } catch (err) {
      console.warn('[push] enable failed:', err?.message);
      setError(err?.message || 'Could not enable push notifications');
      setStatus('off');
      return false;
    }
  }, [userId]);

  const disable = useCallback(async () => {
    setError(null);
    setStatus('busy');
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await readWithTimeout(
          signal => supabase.from('push_subscriptions').delete().eq('endpoint', sub.endpoint).abortSignal(signal),
          { label: 'push_subscription_delete' }
        );
        await sub.unsubscribe();
      }
      setStatus('off');
      return true;
    } catch (err) {
      console.warn('[push] disable failed:', err?.message);
      setError(err?.message || 'Could not disable push notifications');
      setStatus('on');
      return false;
    }
  }, []);

  const sendTest = useCallback(async () => {
    setError(null);
    try {
      const invoke = supabase.functions.invoke('send-push', {
        body: { title: 'SiteScope', body: 'Push notifications are working on this device.', url: '/', tag: 'sitescope-test' },
      });
      const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Test notification timed out')), 10000));
      const { data, error: fnError } = await Promise.race([invoke, timeout]);
      if (fnError) throw new Error(fnError.message || 'Test send failed');
      return Number(data?.sent || 0) > 0;
    } catch (err) {
      console.warn('[push] test send failed:', err?.message);
      setError(err?.message || 'Test notification failed');
      return false;
    }
  }, []);

  return { status, error, enable, disable, sendTest };
}
